// PCA via power iteration — ported verbatim from the original Evo Console
// (pilot_evo_dashboard/static/landscape3d.html) so phylogeny + landscape share
// one basis and match the desktop renderer exactly. Deterministic (fixed seed),
// no RNG, so the same lineage yields the same projection.

export interface Basis {
  mean: Float64Array;
  std: Float64Array;
  pc1: Float64Array;
  pc2: Float64Array;
  lam1: number;
  lam2: number;
  explainedPc1: number;
  explainedPc2: number;
  basisSize: number;
}

function normalize(v: Float64Array): Float64Array {
  let n = 0;
  for (let j = 0; j < v.length; j++) n += v[j] * v[j];
  n = Math.sqrt(n) || 1;
  const o = new Float64Array(v.length);
  for (let j = 0; j < v.length; j++) o[j] = v[j] / n;
  return o;
}

function orthogonalize(v: Float64Array, against: Float64Array): Float64Array {
  let d = 0;
  for (let j = 0; j < v.length; j++) d += v[j] * against[j];
  const o = new Float64Array(v.length);
  for (let j = 0; j < v.length; j++) o[j] = v[j] - d * against[j];
  return o;
}

export function powerIter(Z: Float64Array[], against: Float64Array | null, iters = 24): Float64Array {
  const D = Z[0].length;
  let v: Float64Array = new Float64Array(D);
  for (let j = 0; j < D; j++) v[j] = Math.sin(j * 1.7 + 3.14); // deterministic seed
  v = normalize(v);
  if (against) v = normalize(orthogonalize(v, against));
  for (let it = 0; it < iters; it++) {
    const u = new Float64Array(Z.length);
    for (let i = 0; i < Z.length; i++) {
      let s = 0;
      const row = Z[i];
      for (let j = 0; j < D; j++) s += row[j] * v[j];
      u[i] = s;
    }
    const w = new Float64Array(D);
    for (let i = 0; i < Z.length; i++) {
      const ui = u[i];
      const row = Z[i];
      for (let j = 0; j < D; j++) w[j] += row[j] * ui;
    }
    v = against ? normalize(orthogonalize(w, against)) : normalize(w);
  }
  return v;
}

export function computeBasis(rows: number[][], iters = 24): Basis {
  const D = rows[0].length;
  const N = rows.length;
  const m = new Float64Array(D);
  const s = new Float64Array(D);
  for (let j = 0; j < D; j++) {
    let t = 0;
    for (const row of rows) t += row[j];
    m[j] = t / rows.length;
  }
  for (let j = 0; j < D; j++) {
    let t = 0;
    for (const row of rows) {
      const d = row[j] - m[j];
      t += d * d;
    }
    s[j] = Math.sqrt(t / rows.length) || 1;
  }
  const Z = rows.map((row) => {
    const o = new Float64Array(D);
    for (let j = 0; j < D; j++) o[j] = (row[j] - m[j]) / s[j];
    return o;
  });
  const pc1 = powerIter(Z, null, iters);
  const pc2 = powerIter(Z, pc1, iters);
  const lam = (v: Float64Array): number => {
    let t = 0;
    for (const row of Z) {
      let p = 0;
      for (let j = 0; j < D; j++) p += row[j] * v[j];
      t += p * p;
    }
    return t;
  };
  const lam1 = lam(pc1);
  const lam2 = lam(pc2);
  const totalVar = N * D;
  return {
    mean: m, std: s, pc1, pc2, lam1, lam2, basisSize: N,
    explainedPc1: lam1 / totalVar, explainedPc2: lam2 / totalVar,
  };
}

export function projectRow(basis: Basis, row: number[]): [number, number] {
  let a = 0;
  let b = 0;
  for (let j = 0; j < row.length; j++) {
    const z = (row[j] - basis.mean[j]) / basis.std[j];
    a += z * basis.pc1[j];
    b += z * basis.pc2[j];
  }
  return [a, b];
}

// Landscape color map (cyan low → yellow high), HSL — matches landscape3d.html.
export function fitnessColorHsl(t: number, lightness?: number): string {
  t = Math.max(0, Math.min(1, t));
  const hue = 210 - (210 - 130) * t;
  const lt = lightness !== undefined ? lightness : 24 + (54 - 24) * t;
  return `hsl(${hue}, 70%, ${lt}%)`;
}

// Phylogeny color map: accent #4a9eff (low) → success #3dba6e (high) — rgb lerp,
// matches index.html's fitnessColor exactly.
export function fitnessColorRgb(t: number): string {
  t = Math.max(0, Math.min(1, t));
  const r = Math.round(0x4a + (0x3d - 0x4a) * t);
  const g = Math.round(0x9e + (0xba - 0x9e) * t);
  const b = Math.round(0xff + (0x6e - 0xff) * t);
  return `rgb(${r},${g},${b})`;
}
